// #294 (Phase B core) — critical events on the dashboard. The server decides (handler: the video's
// highest Graffiti spray-sound score ≥ the camera's group threshold) and marks the event `is_critical`
// with its evidence in `critical_reasons`; an event stays open until someone acknowledges it. A
// critical event keeps its normal type in the data (a sound-only one is `motion_detected`), so its
// title comes from here wherever the dashboard shows one: never "Motion detected" for it.
// Item 408 — on a beta camera the clues can make it critical too: {code: 'clues', clues: [the clue codes that scored]}
// after any spray reason, named "Suspicious person" and the clue words.
import type { CluesCriticalReason, Event, SprayCriticalReason } from '@/types'

/** A translate function: react-i18next's `t` from `useTranslation('common')`, or i18next.t with ns. */
export type Translate = (key: string, options?: Record<string, unknown>) => unknown

/** The fields the title needs: a REST `Event`, or the WebSocket payload of a live event. */
export interface TitleSource {
  event_type?: string
  is_critical?: unknown
  critical_reasons?: unknown
}

export function isCriticalEvent(event: unknown): boolean {
  return !!event && typeof event === 'object' && (event as { is_critical?: unknown }).is_critical === true
}

/** Critical and nobody has acknowledged it yet. */
export function isOpenCritical(event: Event): boolean {
  return isCriticalEvent(event) && !event.acknowledged_at
}

function sprayReason(event: unknown): SprayCriticalReason | null {
  if (!isCriticalEvent(event)) return null
  const reasons = (event as { critical_reasons?: unknown }).critical_reasons
  if (!Array.isArray(reasons)) return null
  const found = reasons.find((r): r is SprayCriticalReason =>
    !!r && typeof r === 'object' && (r as SprayCriticalReason).code === 'spray_sound' && typeof (r as SprayCriticalReason).score === 'number')
  return found ?? null
}

/** The spray-sound score as a whole percentage (0.642 → 64), or null when it is not a critical spray event. */
export function criticalPercent(event: unknown): number | null {
  const reason = sprayReason(event)
  return reason ? Math.round(reason.score * 100) : null
}

/** #408 — the reason written when the clues made the event critical. */
function cluesReason(event: unknown): CluesCriticalReason | null {
  if (!isCriticalEvent(event)) return null
  const reasons = (event as { critical_reasons?: unknown }).critical_reasons
  if (!Array.isArray(reasons)) return null
  return reasons.find((r): r is CluesCriticalReason => !!r && typeof r === 'object' && (r as CluesCriticalReason).code === 'clues') ?? null
}

/** The codes of the reasons the dashboard can name (the chip, the alert bar, the popup): spray first, then the clues. */
export function criticalReasonCodes(event: unknown): Array<'spray_sound' | 'clues'> {
  return [...(sprayReason(event) ? ['spray_sound' as const] : []), ...(cluesReason(event) ? ['clues' as const] : [])]
}

/** #408 — the clue codes with words of their own (`common:critical.clueWords.<code>`): every clue a rule can score. */
const CLUE_WORD_CODES: readonly string[] = ['no_hivis', 'backpack', 'hoodie', 'mask', 'spray_can', 'still', 'staying', 'at_train']

/** #408 — the words of the clues behind a critical event, in the server's order; a code without words is left out, never shown raw. */
export function criticalClueWords(event: unknown, t: Translate): string[] {
  const codes = cluesReason(event)?.clues
  if (!Array.isArray(codes)) return []
  return codes.flatMap((code) => {
    if (typeof code !== 'string' || !CLUE_WORD_CODES.includes(code)) return []
    const word = t(`critical.clueWords.${code}`)
    return typeof word === 'string' && word ? [word] : []
  })
}

/** The reasons of a critical event in words, one line ("Spray sound", "Suspicious person: hood up, face covered", both
 *  when both); empty when it names none. #408 — the clues always read at least "Suspicious person". */
export function criticalReasonText(event: unknown, t: Translate): string {
  return criticalReasonCodes(event).map((code) => {
    if (code !== 'clues') return t(`critical.reasonText.${code}`)
    const words = criticalClueWords(event, t)
    return words.length > 0
      ? t('critical.reasonText.cluesWith', { clues: words.join(String(t('critical.clueWordsSeparator'))) })
      : t('critical.reasonText.clues')
  }).join(' · ')
}

/** The one title of an event on the dashboard: the critical title ("Graffiti spray sound · 64 %") for a
 *  critical spray-sound event, otherwise the translated event type (`fallback` when it has none). */
export function eventTitle(event: TitleSource | null | undefined, t: Translate, fallback?: string): string {
  const type = typeof event?.event_type === 'string' && event.event_type ? event.event_type : 'detection'
  const typeFallback = fallback ?? type.replace(/_/g, ' ')
  // A translator that is not ready (i18next before init) gives no string: fall back, never "undefined".
  const text = (v: unknown) => (typeof v === 'string' && v ? v : typeFallback)
  const percent = criticalPercent(event)
  if (percent !== null) return text(t('critical.title', { percent }))
  if (cluesReason(event)) return text(t('critical.titleClues'))   // #408
  return text(t(`eventTypes.${type}`, { defaultValue: typeFallback }))
}

const newestFirst = (a: Event, b: Event) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at)

/** Recent Events rows: the open critical events first (newest first) — from the open list, so an older
 *  one below the 20 recent rows is not lost, and any open one in the recent rows — then the recent rows
 *  in their own order without those. A row the recent list already shows acknowledged is not pinned
 *  again by a stale copy in the open list: it stays in time order, grey. */
export function pinOpenCriticals(open: Event[], recent: Event[]): Event[] {
  const ackedInRecent = new Set(recent.filter((e) => isCriticalEvent(e) && e.acknowledged_at).map((e) => e.id))
  const pinned = new Map<number, Event>()
  for (const e of open) if (isOpenCritical(e) && !ackedInRecent.has(e.id)) pinned.set(e.id, e)
  for (const e of recent) if (isOpenCritical(e) && !pinned.has(e.id)) pinned.set(e.id, e)
  return [...[...pinned.values()].sort(newestFirst), ...recent.filter((e) => !pinned.has(e.id))]
}
